import { useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AddGroupResources from './elements/AddGroupResources';
import DeleteAccessGroup from '../elements/DeleteAccessGroup';
import { AccessGroupMember } from './AccessGroupMember';
import AddGroupMember from './elements/AddGroupMembers';
import { type DisableButtonProp } from './elements/types/disableButtonProp';
import { type AccessGroupProps } from './elements/types/accessGroupProps';

export const AccessGroup = ({
  accessGroup,
  deleteGroup,
  deleteGroupLoading,
  clientUsers,
  addUsers,
  addUsersLoading,
  removeUser,
  removeUserLoading,
  allRoles,
  addRemoveResourceLoading,
  addRemoveResource,
  user,
  isOpen,
  onOpenChange,
}: AccessGroupProps) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);

  const open = isOpen !== undefined ? isOpen : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    setInternalOpen(newOpen);
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
  };

  const memberCount = accessGroup?.members?.length ?? 0;
  const resourceCount = accessGroup.resources?.length ?? 0;
  return (
    <Collapsible.Root className="mb-6 text-sm" open={open} onOpenChange={handleOpenChange}>
      <div className="border border-gray-200 mx-1 access-group-title px-4 flex py-2 items-center bg-gray-50 rounded-t">
        <Collapsible.Trigger asChild>
          <button className="flex items-center w-1/4 text-left cursor-pointer hover:bg-gray-100 p-1 -ml-1 rounded transition-colors mr-4">
            {open ? (
              <ChevronUp size={18} className="mr-2 text-gray-500 flex-shrink-0" />
            ) : (
              <ChevronDown size={18} className="mr-2 text-gray-500 flex-shrink-0" />
            )}
            <span className="font-medium text-gray-800 truncate">{accessGroup.name}</span>
          </button>
        </Collapsible.Trigger>
        <div className="flex items-center w-1/4">
          {memberCount === 1
            ? t('settings.accessGroup.memberCountSingular', { count: memberCount })
            : t('settings.accessGroup.memberCountPlural', { count: memberCount })}
        </div>
        <div className="flex items-center w-1/4">
          {resourceCount === 1
            ? t('settings.accessGroupv2.resourceCountSingular', { count: resourceCount })
            : t('settings.accessGroupv2.resourceCountPlural', { count: resourceCount })}
        </div>
        <div className="w-1/4 flex flex-row justify-end gap-2">
          <AddGroupMember
            accessGroup={accessGroup}
            users={clientUsers}
            onSubmit={addUsers}
            loading={addUsersLoading}
            roles={allRoles}
            canAssignSuperAdmin={user.isAdmin || user.isSuperAgAdmin}
            disable={disableButton({ user, accessGroup, requiredPermissions: ['access-group:add-users'] })}
          />
          <AddGroupResources
            accessGroup={accessGroup}
            onSubmit={addRemoveResource}
            loading={addRemoveResourceLoading}
            disable={disableButton({
              user,
              accessGroup,
              requiredPermissions: ['access-group:add-resources', 'access-group:remove-resources'],
            })}
          />
          <DeleteAccessGroup
            accessGroup={accessGroup}
            onSubmit={deleteGroup}
            loading={deleteGroupLoading}
            disable={!(user.isAdmin || user.isSuperAgAdmin)}
          />
        </div>
      </div>
      <Collapsible.Content className="data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp overflow-hidden">
        <div className="border-l border-r border-b border-gray-200 mx-1 rounded-b shadow-inner">
          {accessGroup && accessGroup.members && accessGroup.members.length > 0 ? (
            accessGroup.members.map((member, index) => (
              <AccessGroupMember
                key={index}
                member={member}
                accessGroup={accessGroup}
                removeUser={removeUser}
                removeUserLoading={removeUserLoading}
                onSubmit={addUsers}
                loading={addUsersLoading}
                allRoles={allRoles}
                canAssignSuperAdmin={user.isAdmin || user.isSuperAgAdmin}
                disableEditUser={disableButton({ user, accessGroup, requiredPermissions: ['access-group:add-users'] })}
                disableRemoveUser={disableButton({
                  user,
                  accessGroup,
                  requiredPermissions: ['access-group:remove-users'],
                })}
              />
            ))
          ) : (
            <div className="p-4 text-gray-500 text-center italic border-t-0">No members in this group</div>
          )}
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

function disableButton({ user, accessGroup, requiredPermissions }: DisableButtonProp) {
  const role = user.accessGroupRoles.find((x) => x.accessGroupId === accessGroup.id);
  return !(
    user.isAdmin ||
    user.isSuperAgAdmin ||
    role?.isLocalAdmin ||
    role?.roles.flatMap((x) => x.permissions).some((x) => requiredPermissions.includes(x))
  );
}
